import { useEffect, useRef } from 'react';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { useProjectRename } from '~/lib/hooks/useProjectRename';
import { classNames } from '~/utils/classNames';

interface TitleControlProps {
  onTitleChange: (title: string) => void;
}

export const TitleControls = ({ onTitleChange }: TitleControlProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { newTitle, errorMessage, updateTitle, handleChange, handleKeyDown, isChanged } = useProjectRename({
    onTitleChange,
  });

  useEffect(() => {
    /**
     * Focus the input shortly after the component was rendered.
     *
     * We sadly cannot just rely on autoFocus because the component
     * gets rendered slightly before a focusin is done when clicking
     * on the rename button in the context menu.
     */
    setTimeout(() => {
      inputRef.current?.focus();
    });
  }, []);

  const handleKeyDownWithBlur = (event: React.KeyboardEvent<HTMLInputElement>) => {
    handleKeyDown(event);

    if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
      inputRef.current?.blur();
    }
  };

  return (
    <fieldset className="flex flex-col gap-1.5 flex-1">
      <label htmlFor="project-title" className="text-xs font-medium text-start text-bolt-elements-text-secondary">
        Project name
      </label>

      <div className="flex gap-1.5">
        <input
          ref={inputRef}
          value={newTitle}
          id="project-title"
          className={classNames([
            'w-full p-2 border border-bolt-elements-borderColor rounded-md outline-bolt-elements-item-outlineActive',
            { 'border-bolt-elements-item-contentDanger': !!errorMessage },
          ])}
          autoFocus
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          aria-label="Project title"
          aria-describedby={errorMessage ? 'project-title-error' : undefined}
          onChange={handleChange}
          onKeyDown={handleKeyDownWithBlur}
        />
        <LegacyButton
          className="self-end"
          variant="secondary"
          disabled={!isChanged}
          minHeight={38}
          onClick={updateTitle}
        >
          <div className="i-ph:check" />
        </LegacyButton>
      </div>

      {errorMessage && (
        <p id="project-title-error" className="text-xs text-start text-bolt-elements-item-contentDanger">
          {errorMessage}
        </p>
      )}
    </fieldset>
  );
};
